import { UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useRootState, useRootDispatch } from 'store/StateProvider';
import { saveFirebirdData } from 'store/slices/actions';
import { accountSummary } from 'types';
import { useAPIQuery } from '../useAPIQuery';

export function useGetAccountSummary({ selectedProductCustomerId, protocol, endpoint }, enabled = false) {
    const dispatch = useRootDispatch();
    const {
        sourcesData: {
            customerDetails: {
                instanceList,
                customerSfdcOemId,
                creditsPurchasedCurrentTerm,
                creditsAvailableForRolloverCurrentTerm,
                ignorePrepopulateCreditBalance,
                daysRemainingCurrentTerm,
            },
        },
    } = useRootState();

    const url = `/creditsizer/firebird/v2/accounts/account/summary`;
    const method = 'GET';

    const handleSelect = (data: { result: accountSummary[]; statusCode: number; success: boolean }) => {
        if (data.result?.length > 0) {
            const [accountDetails] = data.result;
            const { activeContract } = accountDetails.contractSummary || {};
            const contractType = activeContract?.contractType;
            const totalCreditsPurchased =
                contractType === 'Term Consumption' ? Number(activeContract?.creditDetails?.totalCreditsPurchased) : 0;
            const totalCreditsAvailableForRollover =
                contractType === 'Term Consumption'
                    ? Number(activeContract?.creditDetails?.totalCreditsAvailableForRollover)
                    : 0;
            let endDay = activeContract?.endDay ?? 0;

            const MS_PER_DAY = 24 * 60 * 60 * 1000;
            const currentDay = Math.floor(Date.now() / MS_PER_DAY);

            if (activeContract?.contractType === 'Overages') {
                const termConsumptionContract = accountDetails.creditPools
                    .flatMap((pool) => pool.contracts)
                    .find(
                        (contract) =>
                            contract.sourceId === activeContract?.sourceId &&
                            contract.contractType === 'Term Consumption',
                    );

                if (termConsumptionContract) {
                    endDay = termConsumptionContract.endDay ?? 0;
                }
            }

            const daysDiff = endDay - currentDay;

            let existingData = {};
            if (!+creditsPurchasedCurrentTerm && !ignorePrepopulateCreditBalance) {
                existingData = {
                    creditsPurchasedCurrentTerm: totalCreditsPurchased.toFixed(2),
                };
            }
            if (!+creditsAvailableForRolloverCurrentTerm && !ignorePrepopulateCreditBalance) {
                existingData = {
                    ...existingData,
                    creditsAvailableForRolloverCurrentTerm: totalCreditsAvailableForRollover.toFixed(2),
                };
            }
            if (!+daysRemainingCurrentTerm && !ignorePrepopulateCreditBalance) {
                existingData = {
                    ...existingData,
                    daysRemainingCurrentTerm: daysDiff < 0 ? 0 : daysDiff,
                };
            }

            const parsedData = {
                ...existingData,
                firebirdData: {
                    creditsPurchasedCurrentTerm: totalCreditsPurchased.toFixed(2),
                    creditsAvailableForRolloverCurrentTerm: totalCreditsAvailableForRollover.toFixed(2),
                    daysRemainingCurrentTerm: daysDiff < 0 ? 0 : daysDiff,
                },
            };
            return { ...data, result: parsedData };
        }
        return data;
    };
    const phoenixCloudUrl = instanceList.find((obj) => obj.instanceId === selectedProductCustomerId)?.cloudName;

    const payload = {
        cloudName: phoenixCloudUrl,
        instanceIds: [selectedProductCustomerId],
    };
    const query: UseQueryResult<{ result: {}; statusCode: number; success: boolean }, Error> = useAPIQuery({
        key: ['accountSummary', customerSfdcOemId, selectedProductCustomerId],
        method,
        payload,
        url,
        baseUrl: `${protocol}://${endpoint}`,
        select: handleSelect,
        enabled,
        options: {
            staleTime: 24 * 60 * 60 * 1000, // 24 hour stale time to avoid refetching the same API
        },
    });

    useEffect(() => {
        if (query?.data?.success && !query.isError && enabled) {
            dispatch(saveFirebirdData(query.data.result));
        }
    }, [query.data, query.isError, enabled]);
    return query;
}
